import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from "date-fns";
import { nb } from "date-fns/locale";

export default function InvestorCalendar({ tasks = [], onAddMeeting }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      return isSameDay(new Date(task.due_date), date);
    });
  };

  const selectedDateTasks = selectedDate ? getTasksForDate(selectedDate) : [];

  const statusColors = {
    todo: "bg-blue-100 text-blue-700",
    in_progress: "bg-amber-100 text-amber-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <Card className="p-6 bg-white border-0 shadow-sm lg:col-span-2">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">
            {format(currentMonth, "MMMM yyyy", { locale: nb })}
          </h3>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentMonth(new Date())}
            >
              I dag
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"].map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-slate-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, idx) => {
            const dayTasks = getTasksForDate(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());

            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(day)}
                className={`
                  relative p-2 h-20 rounded-lg border-2 transition-all text-left
                  ${isCurrentMonth ? "bg-white" : "bg-slate-50"}
                  ${isSelected ? "border-zinc-900 bg-lime-100" : "border-slate-200"}
                  ${!isCurrentMonth && "text-slate-400"}
                  hover:border-lime-300
                `}
              >
                <div className={`text-sm font-semibold mb-1 ${isToday ? "text-zinc-900" : ""}`}>
                  {format(day, "d")}
                </div>
                {dayTasks.length > 0 && (
                  <div className="space-y-0.5">
                    {dayTasks.slice(0, 2).map((task, i) => (
                      <div
                        key={i}
                        className="text-xs px-1 py-0.5 rounded bg-lime-100 text-zinc-900 truncate"
                        title={task.title}
                      >
                        {task.title}
                      </div>
                    ))}
                    {dayTasks.length > 2 && (
                      <div className="text-xs text-slate-500">+{dayTasks.length - 2} mer</div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Selected Date Details */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-zinc-900" />
            <h3 className="font-semibold text-slate-900">
              {selectedDate ? format(selectedDate, "dd. MMMM", { locale: nb }) : "Velg en dato"}
            </h3>
          </div>
          {selectedDate && onAddMeeting && (
            <Button size="sm" onClick={() => onAddMeeting(selectedDate)}>
              <Plus className="w-3 h-3" />
            </Button>
          )}
        </div>

        {selectedDate ? (
          selectedDateTasks.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              Ingen møter eller oppgaver denne dagen
            </p>
          ) : (
            <div className="space-y-3">
              {selectedDateTasks.map((task) => (
                <div key={task.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-medium text-slate-900 text-sm">{task.title}</h4>
                    <Badge className={`text-xs border-0 ${statusColors[task.status]}`}>
                      {task.status === "todo" ? "Å gjøre" :
                       task.status === "in_progress" ? "Pågår" :
                       task.status === "completed" ? "Fullført" : "Avbrutt"}
                    </Badge>
                  </div>
                  {task.description && (
                    <p className="text-xs text-slate-600 mb-2">{task.description}</p>
                  )}
                  {task.related_to_name && (
                    <p className="text-xs text-slate-500">
                      📊 {task.related_to_name}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-8">
            <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Klikk på en dato for å se detaljer</p>
          </div>
        )}
      </Card>
    </div>
  );
}